import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  generateTerraAuthUrl,
  TerraAuthResponse,
} from "../services/terraService";

// Extend the Window interface for analytics globals.
declare global {
  interface Window {
    fbq?: (action: string, eventName: string, params?: any) => void;
  }
}

interface TerraConnectProps {
  redirectEndpoint?: string;
  onConnectionUpdate?: () => void; // Callback to notify parent of connection changes
  onPopupBlocked?: () => void; // Callback when popup is blocked
  onConnectionStart?: () => void; // Callback when connection process starts
  connectedDeviceCount?: number; // Number of currently connected devices
  maxDevices?: number; // Maximum number of devices allowed (undefined means unlimited)
  showCompleteButton?: boolean; // Whether to show the complete setup button section
  onCompleteSetup?: () => void; // Callback for when user completes setup
  onSkip?: () => void; // Callback for when user chooses to skip
  setupLoading?: boolean; // Loading state for setup completion
}

const TerraConnect: React.FC<TerraConnectProps> = ({
  redirectEndpoint = "/settings/wearable",
  onConnectionUpdate,
  onPopupBlocked,
  onConnectionStart,
  connectedDeviceCount = 0,
  maxDevices, // Remove default value - let undefined pass through
  showCompleteButton = false,
  onCompleteSetup,
  onSkip,
  setupLoading = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { currentUser } = useAuth();

  // Debug logging
  console.log("🔍 TerraConnect Debug:", {
    connectedDeviceCount,
    maxDevices,
    isPlatinum: maxDevices === undefined,
    shouldDisable:
      maxDevices !== undefined && connectedDeviceCount >= maxDevices,
    shouldShowLimitReached:
      maxDevices !== undefined && connectedDeviceCount >= maxDevices,
  });

  // State for Terra connection feedback
  const [terraConnectionStatus, setTerraConnectionStatus] = useState<{
    message: string;
    type: "success" | "error" | null;
  }>({ message: "", type: null });

  // Terra authentication callbacks are handled by dedicated pages
  // (/connectionsuccess and /connectionfailed). This component is only
  // responsible for initiating the connection flow.
  //
  // We use the session-URL redirect flow: a Cloud Function generates a
  // session URL via Terra's REST API, and we redirect the popup to that URL.
  // The legacy `https://widget.tryterra.co/widget.js` script is no longer
  // needed and was returning 404.

  const connectTerra = async () => {
    // Check device limit before attempting connection (skip if unlimited)
    if (maxDevices !== undefined && connectedDeviceCount >= maxDevices) {
      setErrorMessage(
        `Device limit reached. You can connect up to ${maxDevices} devices.`
      );
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setTerraConnectionStatus({ message: "", type: null }); // Clear any previous status

    // For iOS Safari compatibility, open popup immediately
    // then redirect it to the actual URL after we get it
    const popup = window.open("", "_blank");

    try {
      const terraAuthResponse: TerraAuthResponse = await generateTerraAuthUrl(
        redirectEndpoint
      );

      if (terraAuthResponse.url) {
        // Check if popup was blocked
        if (!popup || popup.closed || typeof popup.closed === "undefined") {
          // Popup was blocked
          if (onPopupBlocked) {
            onPopupBlocked();
          }
          setErrorMessage(
            "Popup was blocked. Please allow popups for this site and try again."
          );
        } else {
          // Redirect the already-opened popup to the Terra URL
          popup.location.href = terraAuthResponse.url;

          // Notify parent that connection process has started
          if (onConnectionStart) {
            onConnectionStart();
          }
        }
      } else {
        if (popup && !popup.closed) {
          popup.close();
        }
        setErrorMessage("Failed to generate widget URL");
      }
    } catch (error) {
      // Close the popup if there was an error
      if (popup && !popup.closed) {
        popup.close();
      }
      console.error("Error connecting to Terra:", error);
      setErrorMessage(
        error instanceof Error ? error.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="terra-connect">
        {/* Terra Connection Status */}
        {terraConnectionStatus.type && (
          <div
            className={`alert mb-4 ${
              terraConnectionStatus.type === "success"
                ? "alert-success"
                : "alert-error"
            }`}
          >
            <div className="flex items-center">
              {terraConnectionStatus.type === "success" ? (
                <svg
                  className="w-6 h-6 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              <span className="ml-3">{terraConnectionStatus.message}</span>
            </div>
          </div>
        )}

        <button
          onClick={connectTerra}
          disabled={
            loading ||
            (maxDevices !== undefined && connectedDeviceCount >= maxDevices)
          }
          className={`btn min-w-fit whitespace-nowrap px-4 py-2 flex items-center gap-2 text-sm sm:text-base ${
            maxDevices !== undefined && connectedDeviceCount >= maxDevices
              ? "!bg-base-200 !text-base-content/50 cursor-not-allowed"
              : "!bg-base-200 hover:!bg-base-300"
          }`}
        >
          {loading ? (
            <>
              <span className="loading loading-spinner loading-xs"></span>
              <span>Connecting...</span>
            </>
          ) : maxDevices !== undefined && connectedDeviceCount >= maxDevices ? (
            <>
              <span className="font-semibold">Limit Reached</span>
            </>
          ) : (
            <>
              <span className="font-semibold">Connect Device</span>
              <svg
                className="w-4 h-4 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
            </>
          )}
        </button>

        {/* Error message display */}
        {errorMessage && (
          <div className="mt-2 text-red-600 text-sm">{errorMessage}</div>
        )}
      </div>

      {/* Complete Setup Button Section - positioned outside the white box */}
      {showCompleteButton && (
        <>
          <div className="text-center mt-6">
            <button
              onClick={onCompleteSetup}
              className="bg-neutral text-neutral-content rounded-md backdrop-blur-sm py-5 font-heading font-bold btn-lg w-full hover:bg-neutral/60"
              disabled={setupLoading}
            >
              {setupLoading ? "Completing Setup..." : "Complete Setup"}
            </button>
          </div>
        </>
      )}
    </>
  );
};

export default TerraConnect;
